import { useForm as createUseForm } from "@ant-design-vue/use";
import { notification } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { setIsLoading as setIsLoadingApp } from "../../../app.logic";
import { openModal as openModalOtp } from "../../../components/verify-otp/verify-otp.logic";
import i18n from "../../../i18n";
import router from "../../../router";
import store from "../../../store";
import { initUseForm, watchRulesChildren } from "../../../utils/form";
import { createLoading } from "../../../utils/loading";
import { email, required, requiredWhen, sameWhen, validateError } from "../../../utils/validate";
import { createForm as createFormUser } from "../../app/setting/user-mgt/user/user.logic";
import { callPersonType } from "../register/register.section";
import { createConditionForm } from "./register-officer.condition";
import { callRegisterOfficerAttachment, createField } from "./register-officer.section";
import { callNationality, callRace, callReligion } from "../../app/company/mgt-tab/tab-4/tab4.section";

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/security/user";

export const callUserRegisterOfficer = ({ form }) => store.dispatch(`resource/v1/security/user/registerOfficer`, { data: form });

export const onClickSave = ({ data, formParams, otp }) => {
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callUserRegisterOfficer({ form: { user: { ...data.useForm.modelRef, ...formParams }, otp } })
                .finally(() => setIsLoadingApp(false))
                .then(() => {
                    router.push({ name: "login" });
                });
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createForm = ({ props, data, refs }) => {
    return {
        ...createFormUser({ props, data, refs }),
        addresses: [],
        acceptCondition: null,
        isAcceptPdpa: false,
    };
};

export const createRules = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {
        [`${prefix}refDocumentTypePerson`]: [required],
        [`${prefix}idCard`]: [
            required,
            {
                validator: (rule, value) => {
                    if (value && (!modelRef.refRequestType || modelRef.refRequestType?.key === "1")) {
                        const cardKey = () => model.refDocumentTypePerson?.key;
                        return store
                            .dispatch("resource/v1/security/user/hasIdCard", { cardKey: cardKey(), idCard: value })
                            .then(has => {
                                if (has) {
                                    return Promise.reject(i18n.global.t("error.form.hasIdCard"));
                                }
                                return Promise.resolve();
                            });
                    }
                    return Promise.resolve();
                },
                trigger: "blur"
            }
        ],
        [`${prefix}refIssuedBy`]: [requiredWhen({ when: () => !model.id })],
        [`${prefix}outDate`]: [requiredWhen({ when: () => !model.id })],
        [`${prefix}expireDate`]: [requiredWhen({ when: () => !model.id })],
        [`${prefix}birthDate`]: [required],
        [`${prefix}refNationality`]: [required],
        [`${prefix}refRace`]: [required],
        [`${prefix}refReligion`]: [required],
        [`${prefix}refGender`]: [required],
        [`${prefix}refDepartment`]: [required],
        [`${prefix}refJobPosition`]: [required],
        [`${prefix}refOfficerType`]: [required],
        [`${prefix}refOfficerLv`]: [required],
        [`${prefix}refTitle`]: [required],
        [`${prefix}firstNameTh`]: [required],
        [`${prefix}firstNameEn`]: [required],
        [`${prefix}lastNameTh`]: [required],
        [`${prefix}lastNameEn`]: [required],
        [`${prefix}mobile`]: [required],
        [`${prefix}username`]: [required, email, {
            validator: (rule, value, isCheckDraft) => {
                if (value && (!modelRef.refRequestType || modelRef.refRequestType?.key === "1")) {
                    return store.dispatch("resource/v1/security/user/hasUsername", { username: value.trim(), isCheckDraft }).then(has => {
                        if (has) {
                            return Promise.reject(i18n.global.t("error.form.hasUsername"));
                        }
                        return Promise.resolve();
                    });
                }
                return Promise.resolve();
            },
            trigger: "blur"
        }],
        [`${prefix}password`]: [
            {
                validator: (rule, value) => {
                    if (!!modelRef.refStatus?.key && modelRef.refStatus?.key !== "draft") {
                        return Promise.resolve();
                    }
                    if (Array.isArray(value)) {
                        if (value.filter(d => d.isDelete !== true).length === 0) {
                            return Promise.reject(i18n.global.t("error.form.required"));
                        }
                    } else {
                        if (value === undefined || value === null || value?.length === 0 || value?.toString()?.length === 0) {
                            return Promise.reject(i18n.global.t("error.form.required"));
                        }
                    }
                    const minLength = 4;
                    if (value) {
                        if (value.length < minLength) {
                            if (Array.isArray(value)) {
                                return Promise.reject(i18n.global.t("error.form.minLengthArr", { min: minLength }));
                            } else {
                                return Promise.reject(i18n.global.t("error.form.minLength", { min: minLength }));
                            }
                        }
                    }
                    return Promise.resolve();
                },
                trigger: "change"
            }
        ],
        [`${prefix}passwordConfirm`]: [
            sameWhen({
                when: () => {
                    const { useForm } = data;
                    const { modelRef } = useForm;
                    const model = key ? lodash.get(modelRef, key) : modelRef;
                    return model.password === model.passwordConfirm;
                },
                keyLabel: i18n.global.t("model.user.password"),
                sameLabel: i18n.global.t("model.user.passwordConfirm")
            })
        ],
        [`${prefix}acceptCondition`]: [
            {
                validator: (rule, value) => {
                    if (!!modelRef.refStatus?.key && modelRef.refStatus?.key !== "draft") {
                        return Promise.resolve();
                    }
                    if (value !== true) {
                        return Promise.reject(i18n.global.t("error.form.acceptCondition"));
                    }
                    return Promise.resolve();
                },
                trigger: "change"
            }
        ]
    };
    return rules;
};

export const createRulesAttachment = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {};
    model?.attachments?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}attachments.${index}.documentName`] = [required];
            rules[`${prefix}attachments.${index}.size`] = [requiredWhen({ when: () => model.attachments[index].attribute?.isRequiredAttachment })];
        }
    });
    return rules;
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { useForm } = data;
    const { modelRef } = useForm;
    let promise = Promise.resolve({ data: [modelRef] });
    return promise
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = ({ data }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const promises = [
        callPersonType({}),
        callRegisterOfficerAttachment({}),
        callNationality({}),
        callRace({}),
        callReligion({}),
    ];
    return Promise.all(promises).then(arr => {
        data.personTypes = arr[0];
        data.registerAttachments = arr[1];
        data.nationalities = arr[2];
        data.races = arr[3];
        data.religions = arr[4];

        modelRef.registerDate = moment().format("YYYY-MM-DD");
        modelRef.refPersonType = data.personTypes.find(d => d.key === "2");
        modelRef.refNationality = data.nationalities.find(d => d.key === "Thai");
        modelRef.refRace = data.races.find(d => d.key === "099");
        modelRef.refReligion = data.religions.find(d => d.key === "1");
        data.registerAttachments.forEach(item =>
            modelRef.attachments.push({ documentName: item.value, attribute: lodash.cloneDeep(item.attribute) })
        );
    });
};

export const initForm = ({ props, data, refs }) => {
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.attachments.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesAttachment({ props, data, refs }),
        itemsKey: "attachments"
    });
    loadForm({ props, data, refs });
};

export const onClickOpenModalOtp = ({ props, data, refs }) => {
    data.useForm
        .validate()
        .then(() => {
            openModalOtp({
                props: { dataProp: { form: { email: data.useForm.modelRef.email } } },
                onOkCallback: ({ otp, options }) => {
                    onClickSave({ props, data, refs, otp }).then(() => {
                        notification["success"]({ message: i18n.global.t("views.register.success") });
                        options.bind.onCancel();
                    });
                }
            });
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};
